/**
 * Icon Registry - Single source of truth for icon usage across the app.
 *
 * This ensures consistent icon choices for common actions.
 * Always import icons from here rather than react-icons/md directly.
 *
 * Icons are wrapped with a compatibility shim that maps MUI-style props
 * (fontSize="small", color="primary") to react-icons equivalents.
 *
 * @example
 * import { Icons } from '@/theme';
 * <IconButton><Icons.delete /></IconButton>
 */

import { createElement } from 'react';
import type { CSSProperties, ComponentType } from 'react';
import type { IconBaseProps, IconType } from 'react-icons';

import {
  MdDelete,
  MdEdit,
  MdClose,
  MdArrowBack,
  MdAdd,
  MdCheck,
  MdSave,
  MdShare,
  MdContentCopy,
  MdArchive,
  MdUnarchive,
  MdVisibility,
  MdVisibilityOff,
  MdDownload,
  MdFileDownload,
  MdUpload,
  MdCloudUpload,
  MdSearch,
  MdFilterList,
  MdSort,
  MdMoreVert,
  MdMoreHoriz,
  MdExpandMore,
  MdExpandLess,
  MdChevronLeft,
  MdChevronRight,
  MdArrowBackIos,
  MdArrowForwardIos,
  MdLockOutline,
  MdRefresh,
  MdSettings,
  MdAdminPanelSettings,
  MdInfoOutline,
  MdInfo,
  MdPermMedia,
  MdWarning,
  MdError,
  MdCheckCircle,
  MdHelpOutline,
  MdPersonAdd,
  MdGroup,
  MdLink,
  MdQrCode2,
  MdLabel,
  MdPublish,
  MdPerson,
  MdHistory,
  MdPause,
  MdPeople,
  MdAddBox,
  MdMergeType,
  MdCancel,
  MdSend,
  MdSchedule,
  MdScheduleSend,
  MdReplay,
  MdOpenInNew,
  MdZoomIn,
  MdFlag,
  MdDragIndicator,
  MdWifiOff,
  MdHome,
  MdThumbsUpDown,
  MdLinearScale,
  MdRadioButtonChecked,
  MdCheckBox,
  MdShortText,
  MdQuiz,
  MdFormatListNumbered,
  MdCompareArrows,
  MdTitle,
  MdPhoto,
  MdVideocam,
  MdVolumeUp,
  MdDescription,
  MdWorkspacePremium,
  MdImage,
  MdHourglassEmpty,
  MdAssignment,
  MdEmail,
  MdPhone,
  MdTableChart,
  MdPictureAsPdf,
  MdInsertDriveFile,
  MdAttachFile,
  MdAudiotrack,
  MdCreateNewFolder,
  MdCallMerge,
  MdFileOpen,
  MdSpeakerNotesOff,
  MdAccountCircle,
  MdVideoLibrary,
  MdAudioFile,
  MdSms,
  MdMonitor,
  MdChecklistRtl,
  MdArrowDropDown,
  MdArrowDropUp,
  MdRadioButtonUnchecked,
  MdLockReset,
  MdArrowForward,
  MdTimer,
  MdStars,
  MdDoNotDisturb,
  MdPhotoCamera,
  MdMessage,
  MdLock,
  MdPublic,
  MdVerifiedUser,
  MdNotificationsOff,
  MdDoneAll,
  MdTimerOff,
  MdFlagCircle,
  MdAccessTime,
  MdPlayArrow,
  MdNoEncryption,
  MdLockPerson,
  MdZoomOut,
  MdArticle,
  MdChecklist,
  MdPersonRemove,
  MdRemoveCircleOutline,
  MdAddCircleOutline,
  MdCalendarMonth,
  MdAutorenew,
  MdCached,
  MdClear,
  MdErrorOutline,
  MdCheckCircleOutline,
  MdAlarm,
  MdAlarmOff,
  MdDashboard,
  MdAccountBox,
  MdNotifications,
  MdBusiness,
  MdPowerSettingsNew,
  MdNotes,
  MdUndo,
  MdMenu,
  MdPlayCircle,
  MdTouchApp,
  MdCropFree,
  MdAddPhotoAlternate,
  MdNearMe,
  MdCircle,
  MdSwapHoriz,
  MdHub,
  MdHandyman,
  MdTune,
  MdAutoAwesome,
  MdLinkOff,
  MdLogin,
  MdSelectAll,
  MdDeselect,
  MdFolderOpen,
  MdBarChart,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdWarningAmber,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';

// Maps MUI fontSize string variants to pixel sizes
const FONT_SIZE_PX: Record<string, number> = {
  small: 20,
  medium: 24,
  large: 35,
};

// Maps MUI named colors to CSS color values
const NAMED_COLORS: Record<string, string> = {
  primary: '#1E91D6',
  secondary: '#1E91D6',
  error: '#C42021',
  warning: '#f59e0b',
  success: '#5CB85C',
  info: '#1E91D6',
  action: '#757575',
  disabled: '#bdbdbd',
  inherit: 'currentColor',
};

/**
 * Props the shim translates, plus anything else the caller wants on the SVG.
 *
 * The passthrough matters: this wrapper used to accept exactly four props and
 * silently swallow the rest, so an icon rendered through `Icons.*` could not be
 * given an `aria-label`, a `title`, a `role`, or a `data-testid`. That made
 * every meaningful icon — a success/error status glyph, say — invisible to
 * assistive tech AND unaddressable from a test, which is not a policy anyone
 * chose; it fell out of the MUI -> react-icons migration. `IconBaseProps`
 * extends SVG attributes, and react-icons spreads them onto the rendered
 * `<svg>`, so forwarding is all that is required.
 *
 * `color` and `size` are omitted from the inherited props because the shim
 * owns them: `color` is remapped through NAMED_COLORS, and `size` is derived
 * from MUI's `fontSize` scale.
 */
interface MuiIconCompat extends Omit<IconBaseProps, 'color' | 'size'> {
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
  color?: string;
  style?: CSSProperties;
  className?: string;
}

// Wraps a react-icons component with MUI prop compatibility
function compat(RIcon: IconType): ComponentType<MuiIconCompat> {
  function Wrapped({ fontSize, color, style, className, ...rest }: MuiIconCompat) {
    const size = fontSize && fontSize !== 'inherit' ? (FONT_SIZE_PX[fontSize] ?? 24) : undefined;
    const resolvedColor = color ? (NAMED_COLORS[color] ?? color) : undefined;
    return createElement(RIcon, { size, color: resolvedColor, style, className, ...rest });
  }
  return Wrapped;
}

/**
 * Standardized icons for common actions.
 *
 * Usage rules:
 * - delete: Always use for destructive removal actions
 * - close: Use for dismissing dialogs, panels, popups
 * - back: Use for navigation back to previous view
 * - cancel: Use for canceling an in-progress action (not dismissing)
 */
export const Icons = {
  // Destructive actions
  delete: compat(MdDelete),
  archive: compat(MdArchive),
  unarchive: compat(MdUnarchive),

  // CRUD actions
  add: compat(MdAdd),
  create: compat(MdAddBox),
  edit: compat(MdEdit),
  save: compat(MdSave),
  copy: compat(MdContentCopy),
  merge: compat(MdMergeType),

  // Navigation & dismissal
  close: compat(MdClose),
  back: compat(MdArrowBack),
  home: compat(MdHome),
  expandMore: compat(MdExpandMore),
  expandLess: compat(MdExpandLess),
  chevronLeft: compat(MdChevronLeft),
  chevronRight: compat(MdChevronRight),
  arrowBackIos: compat(MdArrowBackIos),
  arrowForwardIos: compat(MdArrowForwardIos),
  lock: compat(MdLockOutline),

  // Confirmation & status
  confirm: compat(MdCheck),
  check: compat(MdCheck),
  success: compat(MdCheckCircle),
  warning: compat(MdWarning),
  error: compat(MdError),
  info: compat(MdInfoOutline),
  infoFilled: compat(MdInfo),
  help: compat(MdHelpOutline),

  // Module icons
  packets: compat(MdPermMedia),
  contacts: compat(MdPeople),
  analytics: compat(MdBarChart),
  pause: compat(MdPause),
  pending: compat(MdSchedule),

  // Visibility
  preview: compat(MdVisibility),
  show: compat(MdVisibility),
  hide: compat(MdVisibilityOff),

  // Data actions
  search: compat(MdSearch),
  filter: compat(MdFilterList),
  sort: compat(MdSort),
  refresh: compat(MdRefresh),
  download: compat(MdDownload),
  fileDownload: compat(MdFileDownload),
  upload: compat(MdUpload),
  cloudUpload: compat(MdCloudUpload),
  export: compat(MdDownload),
  history: compat(MdHistory),
  send: compat(MdSend),
  schedule: compat(MdSchedule),
  scheduleSend: compat(MdScheduleSend),
  replay: compat(MdReplay),
  openInNew: compat(MdOpenInNew),
  zoomIn: compat(MdZoomIn),
  fullscreen: compat(MdFullscreen),
  fullscreenExit: compat(MdFullscreenExit),
  cancel: compat(MdCancel),
  flag: compat(MdFlag),
  drag: compat(MdDragIndicator),
  offline: compat(MdWifiOff),

  // Menus & more
  moreVert: compat(MdMoreVert),
  moreHoriz: compat(MdMoreHoriz),
  settings: compat(MdSettings),

  // Sharing & collaboration
  share: compat(MdShare),
  invite: compat(MdPersonAdd),
  person: compat(MdPerson),
  group: compat(MdGroup),
  link: compat(MdLink),
  qrCode: compat(MdQrCode2),
  label: compat(MdLabel),
  publish: compat(MdPublish),

  // Question types (for packets and checklists)
  questionTrueFalse: compat(MdCheckCircle),
  questionYesNo: compat(MdThumbsUpDown),
  questionLikert: compat(MdLinearScale),
  questionMultipleChoice: compat(MdRadioButtonChecked),
  questionMultipleResponse: compat(MdCheckBox),
  questionShortAnswer: compat(MdShortText),
  questionOrdering: compat(MdFormatListNumbered),
  questionMatching: compat(MdCompareArrows),
  question: compat(MdQuiz),

  // Component types (for packets)
  componentText: compat(MdTitle),
  componentImage: compat(MdPhoto),
  componentVideo: compat(MdVideocam),
  componentAudio: compat(MdVolumeUp),
  componentDocument: compat(MdDescription),
  componentQuestions: compat(MdQuiz),
  componentLink: compat(MdLink),
  componentCertificate: compat(MdWorkspacePremium),
  componentProcessing: compat(MdHourglassEmpty),
  image: compat(MdImage),

  // Entity types
  checklist: compat(MdAssignment),

  // Contact methods
  email: compat(MdEmail),
  phone: compat(MdPhone),

  // File formats (for export dialogs)
  fileCsv: compat(MdDescription),
  fileExcel: compat(MdTableChart),
  filePdf: compat(MdPictureAsPdf),
  file: compat(MdInsertDriveFile),
  attachment: compat(MdAttachFile),
  audio: compat(MdAudiotrack),

  // Folder actions
  createFolder: compat(MdCreateNewFolder),

  // Communication methods
  sms: compat(MdSms),
  appOnly: compat(MdMonitor),

  // Date/time
  calendar: compat(MdCalendarMonth),
  recurring: compat(MdAutorenew),
  cached: compat(MdCached),

  // Input actions
  clear: compat(MdClear),

  // Error/warning display
  errorOutline: compat(MdErrorOutline),

  // Dashboard/user menu
  dashboard: compat(MdDashboard),
  profile: compat(MdAccountBox),
  accountBox: compat(MdAccountBox),
  notifications: compat(MdNotifications),
  organization: compat(MdBusiness),
  signOut: compat(MdPowerSettingsNew),
  checklists: compat(MdChecklistRtl),
  arrowDropDown: compat(MdArrowDropDown),
  arrowDropUp: compat(MdArrowDropUp),
  radioUnchecked: compat(MdRadioButtonUnchecked),
  yesNo: compat(MdThumbsUpDown),
  lockReset: compat(MdLockReset),
  forward: compat(MdArrowForward),
  next: compat(MdArrowForward),
  timer: compat(MdTimer),
  premium: compat(MdStars),
  ignore: compat(MdDoNotDisturb),
  camera: compat(MdPhotoCamera),
  message: compat(MdMessage),
  lockFilled: compat(MdLock),
  public: compat(MdPublic),
  verified: compat(MdVerifiedUser),
  notificationsOff: compat(MdNotificationsOff),
  doneAll: compat(MdDoneAll),
  timerOff: compat(MdTimerOff),
  flagCircle: compat(MdFlagCircle),
  accessTime: compat(MdAccessTime),
  time: compat(MdAccessTime),
  play: compat(MdPlayArrow),
  noEncryption: compat(MdNoEncryption),
  lockPerson: compat(MdLockPerson),
  zoomOut: compat(MdZoomOut),
  article: compat(MdArticle),
  checklistOutlined: compat(MdChecklist),
  personRemove: compat(MdPersonRemove),
  deactivate: compat(MdRemoveCircleOutline),
  activate: compat(MdAddCircleOutline),
  removeCircle: compat(MdRemoveCircleOutline),
  successOutline: compat(MdCheckCircleOutline),
  alarm: compat(MdAlarm),
  alarmOff: compat(MdAlarmOff),
  required: compat(MdWorkspacePremium),
  callMerge: compat(MdCallMerge),
  fileOpenOutlined: compat(MdFileOpen),
  doNotContact: compat(MdSpeakerNotesOff),
  accountCircle: compat(MdAccountCircle),
  videoLibrary: compat(MdVideoLibrary),
  audioFile: compat(MdAudioFile),
  notes: compat(MdNotes),
  summary: compat(MdNotes),
  undo: compat(MdUndo),
  menu: compat(MdMenu),
  playCircle: compat(MdPlayCircle),

  // Hotspot/region
  hotspot: compat(MdTouchApp),
  region: compat(MdCropFree),
  addPhoto: compat(MdAddPhotoAlternate),
  arrow: compat(MdNearMe),
  circle: compat(MdCircle),

  // Context switching
  switchContext: compat(MdSwapHoriz),
  network: compat(MdHub),

  // Admin
  adminPanelSettings: compat(MdAdminPanelSettings),

  // Staff management
  handyman: compat(MdHandyman),

  // Attributes
  tune: compat(MdTune),

  // Action-specific
  autoAwesome: compat(MdAutoAwesome),
  linkOff: compat(MdLinkOff),
  login: compat(MdLogin),
  selectAll: compat(MdSelectAll),
  deselect: compat(MdDeselect),
  folderOpen: compat(MdFolderOpen),
  keyboardArrowLeft: compat(MdKeyboardArrowLeft),
  keyboardArrowRight: compat(MdKeyboardArrowRight),
  warningAmber: compat(MdWarningAmber),
} as const;

export type IconName = keyof typeof Icons;

/**
 * Question type to icon mapping.
 * Used by QuestionsManager, ChecklistEdit, and question displays.
 */
export const QuestionTypeIcons = {
  true_false: Icons.questionTrueFalse,
  yes_no: Icons.questionYesNo,
  likert: Icons.questionLikert,
  multiple_choice: Icons.questionMultipleChoice,
  multiple_response: Icons.questionMultipleResponse,
  short_answer: Icons.questionShortAnswer,
  hotspot: Icons.hotspot,
  ordering: Icons.questionOrdering,
  matching: Icons.questionMatching,
} as const;

export type QuestionTypeIconKey = keyof typeof QuestionTypeIcons;

/**
 * Safe accessor for question-type icons. Returns the mapped icon component for
 * known types, or `Icons.help` for any type the client does not recognise.
 *
 * Every JSX render site MUST use this accessor instead of indexing
 * `QuestionTypeIcons` directly — an unmapped key produces `undefined`, which
 * React renders as error #130. See #612.
 */
export function getQuestionTypeIcon(type: string): (typeof Icons)[keyof typeof Icons] {
  return (QuestionTypeIcons as Record<string, (typeof Icons)[keyof typeof Icons] | undefined>)[type] ?? Icons.help;
}

/**
 * Component type to icon mapping.
 * Used by PacketEdit, PacketDetail, and component displays.
 */
export const ComponentTypeIcons = {
  text: Icons.componentText,
  image: Icons.componentImage,
  video: Icons.componentVideo,
  audio: Icons.componentAudio,
  document: Icons.componentDocument,
  questions: Icons.componentQuestions,
  link: Icons.componentLink,
  certificate: Icons.componentCertificate,
} as const;

export type ComponentTypeIconKey = keyof typeof ComponentTypeIcons;
